<template>
  <v-container
      :fluid="true"
      fill-height
  >
    <alert-card
        v-if="alert.visible === true"
        v-model:visible="alert.visible"
        :type="alert.type"
        :title="alert.title"
        :text="alert.text"
        :closable="false"
    />
    <car-request-filter
        :year-options="yearItems"
        :filterLoadingStates="filterLoadingStates"
        :filterOptions="filterOptions"
        v-model:client-name="requestFilter.clientName"
        v-model:state="requestFilter.state"
        v-model:show-only-own="requestFilter.showOnlyOwn"
        v-model:created-at-from="requestFilter.createdAtFrom"
        v-model:created-at-to="requestFilter.createdAtTo"
        v-model:manufacturer="carFilter.manufacturer"
        v-model:model="carFilter.model"
        v-model:generation="carFilter.generation"
        v-model:wheel-drive="carFilter.wheelDrive"
        v-model:specification="carFilter.specification"
        v-model:fuel="carFilter.fuel"
        v-model:transmission="carFilter.transmission"
        v-model:engine="carFilter.engine"
        v-model:year-from="carFilter.yearFrom"
        v-model:year-to="carFilter.yearTo"
        v-model:price-from="carFilter.priceFrom"
        v-model:price-to="carFilter.priceTo"
    />
    <car-request-admin-list
        :items="items"
        :skeleton-amount="limit"
        :loading="loading"
    />
    <v-pagination
        v-model="this.page"
        :length="this.pageAmount"
    />
  </v-container>
</template>

<script>

import CarRequestAdminList from "@/components/CarRequest/CarRequestAdminList.vue";
import {inject} from "vue";
import {auth} from "@/common/auth";
import {router} from "@/router";
import {api} from "@/common/api";
import CarRequestFilter from "@/components/CarRequest/CarRequestFilter.vue";
import {CarRequestStatus} from "@/common/enum/car_request_status";
import debounce from "lodash.debounce";
import {dates} from "@/common/dates";
import AlertCard from "@/components/AlertCard.vue";
import {messages} from "@/common/enum/messages";
import {CONFIG} from "@/common/config";
import moment from "moment";

export default {
  name: "AdminCarRequestsView",
  components: {AlertCard, CarRequestFilter, CarRequestAdminList},
  data() {
    return {
      requestFilter: {
        clientName: null,
        state: null,
        showOnlyOwn: null,
        createdAtFrom: null,
        createdAtTo: null,
      },
      filterOptions: {
        states: Object.values(CarRequestStatus),
        manufacturers: [],
        models: [],
        generations: [],
        specifications: [],
        wheelDrives: [],
        filteredSpecifications: [],
        fuels: [],
        transmissions: []
      },
      carFilter: {
        manufacturer: null,
        model: null,
        generation: null,
        wheelDrive: null,
        specification: null,
        fuel: null,
        transmission: null,
        engineFrom: null,
        engineTo: null,
        priceFrom: null,
        priceTo: null,
        yearFrom: null,
        yearTo: null
      },
      filterLoadingStates: {
        manufacturers: true,
        models: false,
        generations: false,
        specifications: false,
        fuels: true,
        transmissions: true
      },
      requestStates: {
        carsRequest: null,
        manufacturersRequest: null,
        modelsRequest: null,
        generationsRequest: null,
        specificationsRequest: null,
        fuelsRequest: null,
        transmissionsRequest: null
      },
      items: [],
      page: 1,
      limit: 10,
      total: 1,
      carsRequestsRequestState: null,
      loading: false,
      alert: {
        initiator: null,
        visible: false,
        type: 'error',
        title: 'Error',
        text: ''
      }
    }
  },
  computed: {
    pageAmount() {
      return Math.ceil(this.total / this.limit)
    },
    yearItems() {
      return Array(moment().year() - 1950 + 1)
          .fill(moment().year())
          .map((year, index) => year - index)
    }
  },
  watch: {
    page() {
      this.loadCarRequests(this.carFilter, this.requestFilter)
    },
    'carFilter.manufacturer'(manufacturer) {
      if (!manufacturer) {
        return
      }
      this.filterOptions.models = []
      this.filterOptions.generations = []
      this.filterOptions.specifications = []
      this.filterOptions.wheelDrives = []
      this.filterOptions.filteredSpecifications = []
      this.carFilter.model = null
      this.carFilter.generation = null
      this.carFilter.wheelDrive = null
      this.carFilter.specification = null
      this.loadModels(manufacturer)
    },
    'carFilter.model'(model) {
      if (!model) {
        return
      }
      this.filterOptions.generations = []
      this.filterOptions.specifications = []
      this.filterOptions.wheelDrives = []
      this.filterOptions.filteredSpecifications = []
      this.carFilter.generation = null
      this.carFilter.wheelDrive = null
      this.carFilter.specification = null
      this.loadGenerations(model)
    },
    'carFilter.generation'(generation) {
      if (!generation) {
        return
      }
      this.filterOptions.specifications = []
      this.filterOptions.wheelDrives = []
      this.filterOptions.filteredSpecifications = []
      this.carFilter.wheelDrive = null
      this.carFilter.specification = null
      this.loadSpecifications(generation)
    },
    'carFilter.wheelDrive'(wheelDrive) {
      if (wheelDrive !== false && wheelDrive !== true) {
        return
      }
      this.carFilter.specification = null
      this.filterOptions.filteredSpecifications = this.filterOptions.specifications.filter((specification) => specification.isFullWD === wheelDrive)
    },
    carFilter: {
      handler: debounce(function(carFilter) {
        this.loadCarRequests(carFilter, this.requestFilter)
      }, 500),
      deep: true,
      immediate: true
    },
    requestFilter: {
      handler: debounce(function(requestFilter) {
        this.loadCarRequests(this.carFilter, requestFilter)
      }, 500),
      deep: true,
      immediate: true
    }
  },
  setup() {
    return {
      switchAuthorizationState: inject('authorization').switchState
    }
  },
  mounted() {
    this.loadStates()
    this.loadManufacturers()
    this.loadFuels()
    this.loadTransmissions()
    this.loadCarRequests(this.carFilter, this.requestFilter)
  },
  methods: {
    //filters
    loadStates() {
      this.filterOptions.states = Object.entries(CarRequestStatus).reduce((state, [key, value]) => {
        state.push({'value': key, 'title': value})
        return state
      }, [])
    },
    loadManufacturers() {
      const token = auth.getToken()
      if (token == null) {
        this.switchAuthorizationState()
        router.push({name: 'sign-in', state: {redirectTo: 'home'}})
        return
      }

      if (this.requestStates.manufacturersRequest && !this.requestStates.manufacturersRequest.signal.aborted) {
        this.requestStates.manufacturersRequest.abort()
      }

      this.requestStates.manufacturersRequest = new AbortController()
      this.filterLoadingStates.manufacturers = true
      api.getFilters(token, 'manufacturers', {}, this.requestStates.manufacturersRequest.signal, (response) => {
        this.filterLoadingStates.manufacturers = false
        if (response?.data?.status === 'error') {
          this.alert.initiator = 'manufacturer'
          this.alert.visible = true
          this.alert.type = 'error'
          this.alert.title = 'Error'
          this.alert.text = response.data?.message?.length === 0 ? messages.en.admin_orders_view.manufacturers_loading_error : response.data.message
          return
        }

        this.resetAlert('manufacturer')
        const filters = response.data.filters
        this.filterOptions.manufacturers = filters.map(manufacturer => {
          return {
            value: manufacturer.id,
            title: manufacturer.english_name
          }
        })
      })
    },
    loadModels(manufacturer) {
      const token = auth.getToken()
      if (token == null) {
        this.switchAuthorizationState()
        router.push({name: 'sign-in', state: {redirectTo: 'home'}})
        return
      }

      if (this.requestStates.modelsRequest && !this.requestStates.modelsRequest.signal.aborted) {
        this.requestStates.modelsRequest.abort()
      }

      this.requestStates.modelsRequest = new AbortController()
      this.filterLoadingStates.models = true
      api.getFilters(token, 'models', {manufacturer_id: manufacturer}, this.requestStates.modelsRequest.signal, (response) => {
        this.filterLoadingStates.models = false
        if (response?.data?.status === 'error') {
          this.alert.initiator = 'model'
          this.alert.visible = true
          this.alert.type = 'error'
          this.alert.title = 'Error'
          this.alert.text = response.data?.message?.length === 0 ? messages.en.admin_orders_view.models_loading_error : response.data.message
          return
        }

        this.resetAlert('model')
        const filters = response.data.filters
        this.filterOptions.models = filters.map(model => {
          return {
            value: model.id,
            title: model.english_name
          }
        })
      })
    },
    loadGenerations(model) {
      const token = auth.getToken()
      if (token == null) {
        this.switchAuthorizationState()
        router.push({name: 'sign-in', state: {redirectTo: 'home'}})
        return
      }

      if (this.requestStates.generationsRequest && !this.requestStates.generationsRequest.signal.aborted) {
        this.requestStates.generationsRequest.abort()
      }

      this.requestStates.generationsRequest = new AbortController()
      this.filterLoadingStates.generations = true
      api.getFilters(token, 'generations', {model_id: model}, this.requestStates.generationsRequest.signal, (response) => {
        this.filterLoadingStates.generations = false
        if (response?.data?.status === 'error') {
          this.alert.initiator = 'generations'
          this.alert.visible = true
          this.alert.type = 'error'
          this.alert.title = 'Error'
          this.alert.text = response.data?.message?.length === 0 ? messages.en.cars_view.generations_loading_error : response.data.message
          return
        }

        this.resetAlert('generation')
        const filters = response.data.filters
        this.filterOptions.generations = filters.map(generation => {
          return {
            value: generation.id,
            title: generation.english_name
          }
        })
      })
    },
    loadSpecifications(generation) {
      const token = auth.getToken()
      if (token == null) {
        this.switchAuthorizationState()
        router.push({name: 'sign-in', state: {redirectTo: 'home'}})
        return
      }

      if (this.requestStates.specificationsRequest && !this.requestStates.specificationsRequest.signal.aborted) {
        this.requestStates.specificationsRequest.abort()
      }

      this.requestStates.specificationsRequest = new AbortController()
      this.filterLoadingStates.specifications = true
      api.getFilters(token, 'specifications', {generation_id: generation}, this.requestStates.specificationsRequest.signal, (response) => {
        this.filterLoadingStates.specifications = false
        if (response?.data?.status === 'error') {
          this.alert.initiator = 'wheel-drives'
          this.alert.visible = true
          this.alert.type = 'error'
          this.alert.title = 'Error'
          this.alert.text = response.data?.message?.length === 0 ? messages.en.cars_view.specifications_loading_error : response.data.message
          return
        }

        this.resetAlert('specifications')
        const filters = response.data.filters
        this.filterOptions.specifications = filters.map(specification => {
          return {
            value: specification.id,
            title: specification.english_name,
            isFullWD: specification.is_full_w_d
          }
        })

        const self = this;
        filters.forEach(function (filter) {
          if (self.filterOptions.wheelDrives.filter(wheelDrive => filter.is_full_w_d == wheelDrive.value).length === 0) {
            self.filterOptions.wheelDrives.push({
              value: filter.is_full_w_d,
              title: filter.is_full_w_d ? '4WD' : '2WD',
            })
          }
        })
      })
    },
    loadFuels() {
      const token = auth.getToken()
      if (token == null) {
        this.switchAuthorizationState()
        router.push({name: 'sign-in', state: {redirectTo: 'home'}})
        return
      }

      if (this.requestStates.fuelsRequest && !this.requestStates.fuelsRequest.signal.aborted) {
        this.requestStates.fuelsRequest.abort()
      }

      this.requestStates.fuelsRequest = new AbortController()
      this.filterLoadingStates.fuels = true
      api.getFilters(token, 'fuels', {}, this.requestStates.fuelsRequest.signal, (response) => {
        this.filterLoadingStates.fuels = false
        if (response?.data?.status === 'error') {
          this.alert.initiator = 'fuel'
          this.alert.visible = true
          this.alert.type = 'error'
          this.alert.title = 'Error'
          this.alert.text = response.data?.message?.length === 0 ? messages.en.admin_orders_view.fuels_loading_error : response.data.message
          return
        }

        this.resetAlert('fuel')
        const filters = response.data.filters
        this.filterOptions.fuels = filters.map(fuel => {
          return {
            value: fuel.id,
            title: fuel.english_name
          }
        })
      })
    },
    loadTransmissions() {
      const token = auth.getToken()
      if (token == null) {
        this.switchAuthorizationState()
        router.push({name: 'sign-in', state: {redirectTo: 'home'}})
        return
      }

      if (this.requestStates.transmissionsRequest && !this.requestStates.transmissionsRequest.signal.aborted) {
        this.requestStates.transmissionsRequest.abort()
      }

      this.requestStates.transmissionsRequest = new AbortController()
      this.filterLoadingStates.transmissions = true
      api.getFilters(token, 'transmissions', {}, this.requestStates.transmissionsRequest.signal, (response) => {
        this.filterLoadingStates.transmissions = false
        if (response?.data?.status === 'error') {
          this.alert.initiator = 'transmission'
          this.alert.visible = true
          this.alert.type = 'error'
          this.alert.title = 'Error'
          this.alert.text = response.data?.message?.length === 0 ? messages.en.admin_orders_view.transmissions_loading_error : response.data.message
          return
        }

        this.resetAlert('transmission')
        const filters = response.data.filters
        this.filterOptions.transmissions = filters.map(transmission => {
          return {
            value: transmission.id,
            title: transmission.english_name
          }
        })
      })
    },

    //CarRequests
    loadCarRequests(carFilter, requestFilter) {
      const token = auth.getToken()
      if (token == null) {
        this.switchAuthorizationState()
        router.push({name: 'sign-in', state: {redirectTo: 'home'}})
        return
      }

      if (this.carsRequestsRequestState && !this.carsRequestsRequestState.signal.aborted) {
        this.carsRequestsRequestState.abort()
      }

      this.carsRequestsRequestState = new AbortController()
      this.loading = true
      api.getCarRequests(token, {
        page: this.page,
        limit: this.limit,
        manufacturer_id: carFilter.manufacturer,
        model_id: carFilter.model,
        generation_id: carFilter.generation,
        full_wd: carFilter.wheelDrive,
        specification_id: carFilter.specification,
        fuel_id: carFilter.fuel,
        transmission_id: carFilter.transmission,
        engine_from: carFilter.engineFrom,
        engine_to: carFilter.engineTo,
        year_from: carFilter.yearFrom,
        year_to: carFilter.yearTo,
        price_from: carFilter.priceFrom,
        price_to: carFilter.priceTo,
        client_name: requestFilter.clientName,
        state: requestFilter.state,
        show_only_own: requestFilter.showOnlyOwn,
        created_at_from: requestFilter.createdAtFrom,
        created_at_to: requestFilter.createdAtTo
      }, this.carsRequestsRequestState.signal, this.setCarRequests)
    },
    setCarRequests(response) {
      this.loading = false
      if (response?.data?.status === 'error') {
        this.alert.initiator = 'request'
        this.alert.visible = true
        this.alert.type = 'error'
        this.alert.title = 'Error'
        this.alert.text = response.data?.message?.length === 0 ? messages.en.admin_orders_view.requests_loading_error : response.data.message
        return
      }

      this.resetAlert('request')
      const carRequests = response.data.car_requests
      this.total = response.data.total
      this.items = carRequests.map(carRequest => {
        return {
          id: carRequest.id,
          title: carRequest.car.manufacturer.english_name + ' ' + carRequest.car.model.english_name + ', ' + carRequest.car.year.value,
          createdAt: dates.mapToDateTime(carRequest.created_at),
          mainPhoto: CONFIG.API_HOST + '/api/v1/car-photos/' + carRequest.car.main_photo.id,
          clientName:  carRequest.user.profile !== null && carRequest.user.profile.first_name !== null && carRequest.user.profile.last_name !== null ? carRequest.user.profile.first_name + ' ' + carRequest.user.profile.last_name : carRequest.user.username,
          managerName: carRequest.manager ? carRequest.manager.profile.first_name + ' ' + carRequest.manager.profile.last_name : 'Not assigned',
          engine: carRequest.car.engine,
          transmission: carRequest.car.transmission.english_name,
          fuel: carRequest.car.fuel_type.english_name,
          millage: carRequest.car.mileage.value,
          vin: carRequest.car.vin,
          description: carRequest.car.description,
          messageIndicator: false,
          status: CarRequestStatus[carRequest.last_status.state],
          prices: carRequest.car.public_prices,
          deliveryPrice: carRequest.last_status.delivery_price.value != 0 ? carRequest.last_status.delivery_price.value : null,
          paperworkPrice: carRequest.last_status.paper_work_price.value != 0 ? carRequest.last_status.paper_work_price.value : null,
        }
      })
    },
    resetAlert(initiator) {
      if (this.alert.initiator === initiator) {
        this.alert.visible = false
      }
    }
  }
}
</script>

<style scoped>

</style>